/**
 * =============================================================================
 * researchEntry — reading a registry record
 * =============================================================================
 *
 * The research inbox stopped being a journal-submission queue in October 2026
 * and became a REGISTRY of research that has already been published, or of
 * studies. Full analysis and the agreed API shape are in the website repo:
 * DPA-FE/docs/researchesPlanning/.
 *
 * Three facts about a registry record are awkward enough to be worth one
 * definition rather than several, and all three are shared between the record
 * TABLE and the record DETAIL screen:
 *
 *  1. `submissionKind` arrives as the enum string `research` or `study`. It is
 *     never translated server-side, so the Arabic label is produced here.
 *  2. A record's title lives in `researchTitle` OR `studyTitle` depending on
 *     that enum — never both.
 *  3. `fullName` replaced `firstName`/`middleName`/`lastName`. Records created
 *     BEFORE the change still carry the old three, so both shapes have to read
 *     correctly or the 14 historic entries lose their applicant name in the UI.
 *     That back-compatibility is the main reason this file exists.
 */

export const KIND_RESEARCH = 'research';
export const KIND_STUDY = 'study';

/** What the CMS shows when a field is genuinely absent. */
const EMPTY = '-';

const KIND_LABELS = {
  [KIND_RESEARCH]: 'بحث علمي',
  [KIND_STUDY]: 'دراسة',
};

/**
 * The Arabic label for a submission kind.
 *
 * Returns the dash for a record created before the registry change, which has
 * no `submissionKind` at all — not "unknown", which would read as an error
 * rather than as "this record predates the field".
 *
 * @param {string} [kind]
 * @returns {string}
 */
export const submissionKindLabel = (kind) =>
  KIND_LABELS[String(kind || '').toLowerCase()] || EMPTY;

/**
 * The applicant's name, from either record shape.
 *
 * @param {object} [entry]
 * @returns {string}
 */
export const researchFullName = (entry) => {
  if (!entry) return EMPTY;

  if (entry.fullName) return String(entry.fullName).trim();

  // Pre-registry record: rebuild from the three columns that used to exist.
  // `middleName` was null on 11 of the 14 historic rows, so it is filtered
  // rather than joined blindly — otherwise the name renders with a double
  // space in the middle.
  const joined = [entry.firstName, entry.middleName, entry.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();

  return joined || EMPTY;
};

/**
 * The entry's title.
 *
 * The specification sent to the backend asked for `researchTitle` and
 * `studyTitle` as two separate fields, one per branch. What SHIPPED is a single
 * `title` carrying whichever one applies — a reasonable simplification, since
 * no entry can be both. Both spellings are read so the screens work against the
 * deployed API and against the spec as written.
 *
 * @param {object} [entry]
 * @returns {string}
 */
export const researchEntryTitle = (entry) => {
  if (!entry) return EMPTY;

  const branchTitle =
    entry.submissionKind === KIND_STUDY ? entry.studyTitle : entry.researchTitle;

  return entry.title || branchTitle || EMPTY;
};

/**
 * The entry's identifier.
 *
 * Shipped as `id`; the specification asked for `researchId`, and the older
 * pre-registry records use that name. Read both — this is the React list key
 * and the route parameter for the detail screen, so an undefined here makes
 * every row unopenable and collapses them into one key.
 *
 * @param {object} [entry]
 * @returns {string|undefined}
 */
export const researchEntryId = (entry) => entry?.id || entry?.researchId;

/**
 * When the entry was submitted.
 *
 * Shipped as `submittedOn`; pre-registry records carry `createdOn`.
 *
 * @param {object} [entry]
 * @returns {string|undefined}
 */
export const researchSubmittedOn = (entry) =>
  entry?.submittedOn || entry?.createdOn;

/**
 * The applicant's stated affiliation, as one readable line.
 *
 * The three booleans are not mutually exclusive — somebody may be an employee
 * AND a faculty member — so every true answer is listed, with its follow-up in
 * brackets where one was given.
 *
 * @param {object} [entry]
 * @returns {string}
 */
export const researchAffiliation = (entry) => {
  if (!entry) return EMPTY;

  const parts = [];
  if (entry.isDubaiPoliceEmployee) parts.push('من موظفي شرطة دبي');
  if (entry.isFacultyMember) {
    parts.push(
      entry.academicDepartment
        ? `عضو هيئة تدريس (${entry.academicDepartment})`
        : 'عضو هيئة تدريس'
    );
  }
  if (entry.isAcademyStudent) {
    // Read through the helper: the two sides spell this differently. See the
    // note on `researchSpecialisation`.
    const specialisation = researchSpecialisation(entry);
    parts.push(
      specialisation
        ? `طالب في الأكاديمية (${specialisation})`
        : 'طالب في الأكاديمية'
    );
  }

  return parts.length ? parts.join(' · ') : EMPTY;
};

/**
 * The seven sectors `researchType` used to hold, in enum order.
 *
 * Captured from `GET /api/Research/GetOpportunityTypes` on 2026-11-03, which is
 * still the authority for the RESEARCH OPPORTUNITIES module — this copy exists
 * only to read HISTORIC registry records, and is deliberately not fetched: a
 * detail screen should not make a network call to decode a value that no new
 * record will ever carry again.
 */
const LEGACY_RESEARCH_SECTORS = Object.freeze([
  'قطاع شؤون التميز والريادة',
  'قطاع شؤون العمليات',
  'قطاع شؤون الإدارة',
  'قطاع التحقيق الجنائي',
  'قطاع شؤون إسعاد المجتمع والدعم اللوجستي',
  'قطاع شؤون الموانئ',
  'قطاع شؤون الأكاديمية والتدريب',
]);

/**
 * «مجال البحث» — the research field, from either record shape.
 *
 * ⚠ THIS SCREEN WAS READING A FIELD THAT DOES NOT EXIST. It took
 * `entry.researchTypeDisplay`, and no endpoint has ever returned that name —
 * the detail response carries `researchType` (confirmed against the deployed
 * API on 2026-11-03, and against the fixture pinned in this helper's own test
 * since 2026-10-14). `Fact` hides a row whose value is undefined, so
 * «مجال البحث» silently did not render AT ALL on the record detail screen. It
 * was invisible rather than wrong, which is why it was never reported.
 *
 * TWO RECORD SHAPES, because the website changed the field's TYPE on
 * 2026-11-03 (DPA-FE `src/features/submitResearch`):
 *
 *   BEFORE  a `<Select>` over the sector enum, so `researchType` was a NUMBER
 *           0-6 and `2` meant «قطاع شؤون الإدارة».
 *   AFTER   free text, so it is whatever the researcher typed.
 *
 * Showing a bare `2` to an operator is useless, so a legacy numeric value is
 * decoded and anything else is shown as written.
 *
 * ⚠ THE AMBIGUITY IS DELIBERATE AND ONE-SIDED. A researcher who types exactly
 * "0".."6" and nothing else is read as a legacy sector. That is accepted: a
 * lone digit is not a plausible research field, every record written before the
 * change really is an enum, and the alternative — printing «2» — is wrong for
 * the common case in order to be right for one that should not occur. It
 * applies only to single digits IN RANGE: "2222222222222" is out of range and
 * comes back untouched.
 *
 * @param {object} [entry]
 * @returns {string} the label, or the dash sentinel when absent
 */
export const researchFieldLabel = (entry) => {
  const raw = entry?.researchType;

  // `0` is a REAL answer here, so this cannot be a falsy check.
  if (raw === null || raw === undefined) return EMPTY;

  const text = String(raw).trim();
  if (!text) return EMPTY;

  if (/^\d+$/.test(text)) {
    const index = Number(text);
    if (index < LEGACY_RESEARCH_SECTORS.length) {
      return LEGACY_RESEARCH_SECTORS[index];
    }
  }

  return text;
};

/**
 * The applicant's stated specialisation.
 *
 * ⚠ THE TWO SIDES SPELL THIS DIFFERENTLY. The backend team asked for
 * `specialization` (z) and the website posts that; the API answers with
 * `specialisation` (s). Reading one spelling loses the value in whichever
 * direction is wrong — silently, as a blank line with no error anywhere. Both
 * are read until the backend settles on one.
 *
 * @param {object} [entry]
 * @returns {string|undefined}
 */
export const researchSpecialisation = (entry) =>
  entry?.specialisation || entry?.specialization;

/**
 * The attachments on an entry, in the order an operator wants them.
 *
 * THREE DELIVERY MECHANISMS, because this has now changed twice under us:
 *
 *   1  `researchDocument_FileName` + `_Content` (base64) + `_ContentType`,
 *      from a separate documents endpoint. The original.
 *   2  `researchDocumentFileName` + one shared `blobUrl` prefix on the record.
 *      No base64, and the underscores gone from the keys.
 *   3  `researchDocument: { url, fileName }` — an OBJECT per attachment, which
 *      is what the backend delivered on 2026-10-19 ("`cv.url` opens the PDF").
 *
 * No two of them share a key or a transport, so a card written for one renders
 * nothing at all for the others — the symptom each time has been an
 * attachments panel that is simply empty. All three are read here so the
 * screens never need to know which era a record is from.
 *
 * @param {object} [entry]  the record, which now carries the file fields itself
 * @param {object} [legacy] the older separate documents payload, if fetched
 * @returns {Array<object>}
 */
export const researchAttachments = (entry, legacy) => {
  const src = { ...(legacy || {}), ...(entry || {}) };
  const isStudy = src.submissionKind === KIND_STUDY;

  // Only the middle shape needs a prefix. Trailing slash trimmed so joining is
  // predictable however the API spells it.
  const base = String(src.blobUrl || '').replace(/\/+$/, '');

  /**
   * One slot, read from whichever of the three shapes this record is in.
   *
   * `slot` is the delivered object (`cv`, `researchDocument`); `flat` and
   * `underscored` are the two older spellings of the same file.
   */
  const read = (key, label, slot, flat, underscored) => {
    const object = src[slot];
    const fileName =
      object?.fileName || object?.name || src[flat] || src[underscored];
    const url = object?.url || (base && fileName ? `${base}/${fileName}` : '');

    return {
      key,
      label,
      // An object may carry a URL and no filename. A download still needs
      // something to call the file, so the key is the floor.
      fileName: fileName || (url ? key : ''),
      url,
      base64: src[`${underscored.split('_')[0]}_Content`],
      mimeType: src[`${underscored.split('_')[0]}_ContentType`],
    };
  };

  const slots = [
    read(
      'researchDocument',
      isStudy ? 'وثيقة الدراسة' : 'وثيقة البحث',
      'researchDocument',
      'researchDocumentFileName',
      'researchDocument_FileName'
    ),
    read(
      'profilePic',
      'صورة الملف الشخصي',
      'profilePic',
      'profilePicFileName',
      'profilePic_FileName'
    ),
    read('cv', 'السيرة الذاتية', 'cv', 'cvFileName', 'cV_FileName'),
    read(
      'englishAbstract',
      'الملخص الإنجليزي',
      'englishAbstract',
      'englishAbstractFileName',
      'englishAbstract_FileName'
    ),
  ];

  // A slot with neither a file to name nor an address to fetch is not an
  // attachment, and must not render as an empty card.
  return slots.filter((s) => s.url || s.fileName || s.base64);
};

/**
 * A resolvable address for a DOI or an ORCID iD, or '' when there is no value.
 *
 * Operators need to click through to the published record; a bare identifier
 * is not a link. Values are stored bare, so the prefix is added here.
 *
 * @param {'doi'|'orcid'} type
 * @param {string} [value]
 * @returns {string}
 */
export const identifierUrl = (type, value) => {
  const id = String(value || '').trim();
  if (!id) return '';
  return type === 'doi' ? `https://doi.org/${id}` : `https://orcid.org/${id}`;
};

export { EMPTY as RESEARCH_EMPTY_VALUE };
